import { fmtShortDate } from "./cardStatementDetail";

/**
 * The statement detail panel for ONE credit card, rendered under that card's
 * rows at the bottom of a clinic's cash flow card.
 *
 * Everything on it is entered elsewhere — the balance and its plan on Monthly
 * Inputs, the charges as scheduled payments drawn from the card — and this panel
 * exists to answer, in one place, the questions the grid's three card rows
 * cannot: what is still to pay, whether it is late, and what is still being
 * charged to the card before it comes due.
 *
 * Every line is also a way IN to the thing behind it: a charge opens its
 * schedule, a payment (or the statement summary) opens its statement, in the
 * same modals the Monthly Inputs and All Payments tabs use. The panel raises the
 * intent and the page owns the modals — it never mutates anything itself, which
 * is what keeps one editor per record no matter where it was opened from.
 *
 * The two lists are the two directions money moves on a card, which is why they
 * sit side by side rather than in one ledger:
 *   Charged to the card  — outflows ON the card, growing what will be owed
 *   Bank → card payments — inflows TO the card, the statement's own splits
 * The payments are the same money the paying clinic's "Credit card payment"
 * outflow row shows; this is the receiving side of it.
 */

// Cents matter here in a way they do not on the grid: a balance split three
// ways lands on 2,683.33 / 2,683.33 / 2,683.34, and rounding all three to the
// dollar makes them look wrong against a total that does not equal their sum.
function money(value, { signed = false } = {}) {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = signed ? (value < 0 ? "−" : "+") : value < 0 ? "−" : "";
  return `${sign}$${abs}`;
}

// Whole dollars for the big summary figures, matching the grid above them.
function wholeMoney(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "−" : "";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString("en-CA")}`;
}

function Pill({ tone = "gray", children, title }) {
  const tones = {
    gray: "bg-palette-gray-subtle text-palette-gray-secondary",
    rose: "bg-palette-rose-soft text-palette-rose-heading",
    amber: "bg-palette-amber-soft text-palette-amber-badge",
    emerald: "bg-palette-emerald-soft text-palette-emerald-text",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone] ?? tones.gray}`}
    >
      {children}
    </span>
  );
}

function Dot({ className }) {
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${className}`} />;
}

/**
 * A list line, rendered as a button when there is something to open and as
 * plain markup when there is not.
 *
 * The element CHANGES with the handler rather than a disabled button being left
 * behind: a viewer without edit rights should not be offered a control at all,
 * and a <button> that does nothing still takes a tab stop and still says
 * "clickable" to a screen reader.
 */
function PencilIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11.5 2.5a1.4 1.4 0 0 1 2 2L5.7 12.3 3 13l.7-2.7 7.8-7.8z" />
    </svg>
  );
}

/**
 * A list line, rendered as a button when there is something to open and as
 * plain markup when there is not.
 *
 * The element CHANGES with the handler rather than a disabled button being left
 * behind: a viewer without edit rights should not be offered a control at all,
 * and a <button> that does nothing still takes a tab stop and still says
 * "clickable" to a screen reader.
 *
 * The whole line is the target, and the pencil on the right is what SAYS so —
 * it is drawn as a span, not a nested button, because a button inside a button
 * is invalid markup and would give the same action two tab stops. It is always
 * visible rather than appearing on hover: an affordance nobody can see until
 * they are already pointing at it does not tell them the row is editable.
 */
function Row({ onClick, title, editLabel, children }) {
  const inner = (
    <div className="flex w-full items-center justify-between gap-3 text-left">{children}</div>
  );
  if (!onClick) {
    return (
      <li className="py-2" title={title}>
        {inner}
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={editLabel}
        title={title ? `${title} — click to edit` : "Click to edit"}
        className="group -mx-2 flex w-full items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-palette-gray-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-palette-indigo-ring"
      >
        {inner}
        <span
          aria-hidden="true"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-palette-gray-divider bg-white text-palette-gray-secondary transition-colors group-hover:border-palette-indigo-border group-hover:text-palette-indigo-badge"
        >
          <PencilIcon />
        </span>
      </button>
    </li>
  );
}

/** One labelled figure in the summary strip. */
function Figure({ label, value, valueClass = "text-palette-gray-primary", sub, big = false }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-palette-gray-secondary">
        {label}
      </div>
      <div
        className={`${big ? "text-3xl" : "text-xl"} font-semibold tabular-nums leading-tight ${valueClass}`}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-palette-gray-secondary">{sub}</div>}
    </div>
  );
}

export default function CardStatementPanel({ detail, onEditCharge, onEditStatement }) {
  if (!detail) return null;
  const { card } = detail;
  // Raised with the whole model, not just an id: a pending panel has no
  // statement to name, and the page needs the CARD to open an empty one for.
  const editStatement = onEditStatement ? () => onEditStatement(detail) : undefined;

  // Card identity line: the account's own name, its last digits, and the clinic
  // it belongs to — enough to tell two of a clinic's cards apart at a glance.
  const subtitle = [
    card.bankName,
    card.accountNumber ? `••${card.accountNumber.slice(-4)}` : null,
    card.clinicCode,
  ]
    .filter(Boolean)
    .join(" · ");

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-7 items-center rounded bg-palette-amber-badge px-2 text-[10px] font-bold uppercase tracking-wider text-white">
          Card
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate font-semibold text-palette-gray-primary">
            {card.label} cashflow
          </div>
          {subtitle && (
            <div className="truncate text-xs text-palette-gray-secondary">{subtitle}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {detail.pending ? (
          <Pill tone="amber">
            <Dot className="bg-palette-amber-badge" />
            {detail.monthLabel} balance pending
          </Pill>
        ) : (
          <Pill tone="gray">{detail.monthLabel} statement</Pill>
        )}
        {editStatement && (
          <button
            type="button"
            onClick={editStatement}
            className="inline-flex items-center gap-1.5 rounded-md border border-palette-gray-divider bg-white px-2.5 py-1 text-xs font-medium text-palette-gray-primary transition-colors hover:border-palette-indigo-border hover:text-palette-indigo-badge focus:outline-none focus-visible:ring-2 focus-visible:ring-palette-indigo-ring"
          >
            <PencilIcon />
            {detail.pending ? `Add ${detail.monthLabel} balance & plan` : "Edit statement"}
          </button>
        )}
      </div>
    </div>
  );

  // No statement covers the window. The charges list is still worth showing —
  // it is what the pending balance will be made of — so the panel keeps its
  // left column and says plainly why the rest is missing.
  if (detail.pending) {
    return (
      <div className="mx-4 mb-4 overflow-hidden rounded-lg border border-palette-amber-border bg-palette-amber-soft/40">
        {header}
        <div className="border-t border-palette-amber-border/60 px-4 py-3 text-sm text-palette-gray-secondary">
          No statement has been entered for {detail.monthLabel} yet — add its balance and payment
          plan on Monthly Inputs and the amounts appear here.
        </div>
        {detail.charges.length > 0 && (
          <div className="border-t border-palette-amber-border/60 bg-white px-4 py-3">
            <ChargeList charges={detail.charges} onEditCharge={onEditCharge} />
          </div>
        )}
      </div>
    );
  }

  const util = detail.utilisation;

  return (
    <div className="mx-4 mb-4 overflow-hidden rounded-lg border border-palette-amber-border bg-palette-amber-soft/40">
      {header}

      {/* ── Summary strip ─────────────────────────────────────── */}
      <div className="border-t border-palette-amber-border/60 bg-white/70 px-4 py-4">
        {detail.overdueDays > 0 && (
          <div className="mb-4 flex gap-2 rounded-md bg-palette-amber-soft px-3 py-2 text-sm text-palette-amber-text">
            <span aria-hidden="true">→</span>
            <p>
              <strong className="font-semibold">
                {wholeMoney(detail.toPay)} went unpaid past {fmtShortDate(detail.dueDate)} —{" "}
                {detail.overdueDays}d overdue.
              </strong>{" "}
              {/* No rate is stored against an account, so the interest this
                  will carry is deliberately not estimated — a made-up rate on a
                  figure people plan against is worse than no figure. */}
              It carries into the next statement and starts accruing interest.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
          <Figure
            big
            label="To pay"
            value={wholeMoney(detail.toPay)}
            valueClass={
              detail.overdueDays > 0 ? "text-smiles-redOrange" : "text-palette-gray-primary"
            }
            sub={
              <>
                {fmtShortDate(detail.statementDate)} → {fmtShortDate(detail.dueDate)}
                {detail.graceDays != null && ` · ${detail.graceDays}-day grace`}
                {detail.planLabel && ` · ${detail.planLabel}`}
              </>
            }
          />
          {detail.overdueDays > 0 && (
            <div className="self-center">
              <Pill tone="rose">
                <Dot className="bg-palette-rose-badge" />
                Overdue {detail.overdueDays}d
              </Pill>
            </div>
          )}
          <Figure label="Statement" value={wholeMoney(detail.balance)} />
          <Figure
            label="Paid so far"
            value={wholeMoney(detail.paidSoFar)}
            valueClass="text-smiles-green"
          />

          {/* Pushed to the far right of the strip: the figures on the left are
              this statement, the meter is the CARD it sits on — a different
              question, and putting the gap between them says so. `ml-auto`
              rather than `justify-between` on the row, which would also spread
              the three figures apart. It falls back into the flow when the
              strip wraps on a narrow screen. */}
          {util && (
            <div className="w-full max-w-xs sm:ml-auto">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-palette-gray-secondary">
                  Card use
                </span>
                <span className="text-sm font-semibold tabular-nums text-palette-gray-primary">
                  {wholeMoney(util.used)} of {wholeMoney(util.limit)}
                </span>
              </div>
              {/* Two lengths in one bar: the solid part is what is drawn on the
                  card TODAY, the pale part behind it how far that reaches by the
                  due date. Nested rather than laid side by side, because the
                  peak CONTAINS today's balance — they are one bar measured at
                  two moments, not two amounts to add up. */}
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-palette-amber-border">
                <div
                  className="h-full rounded-full bg-palette-amber-ring"
                  style={{ width: `${Math.min(Math.max(util.peakPct, 0), 1) * 100}%` }}
                >
                  <div
                    className={`h-full rounded-full ${
                      util.pct >= 0.9
                        ? "bg-palette-rose-badge"
                        : util.pct >= 0.7
                          ? "bg-palette-amber-badge"
                          : "bg-smiles-green"
                    }`}
                    style={{
                      width: `${util.peakPct > 0 ? Math.min(util.pct / util.peakPct, 1) * 100 : 100}%`,
                    }}
                  />
                </div>
              </div>
              <div className="mt-1 text-xs text-palette-gray-secondary">
                {Math.round(util.pct * 100)}% of the limit in use
                {/* The peak is only worth naming while it is still ahead: once
                    the last charge has landed it IS the current figure, and
                    repeating it would read as a second, larger number. */}
                {util.peakPct > util.pct && (
                  <>
                    {" · peaks at "}
                    <span className="font-medium text-palette-gray-primary">
                      {Math.round(util.peakPct * 100)}%
                    </span>
                    {util.peakOn && <> on {fmtShortDate(util.peakOn)}</>}
                  </>
                )}
                {/* Its own clause, not hung off the peak with a dash: payments
                    interleave with these charges, so the peak is usually NOT
                    the sum of them and joining the two implied a total that
                    never appears anywhere. */}
                {util.upcoming > 0 && (
                  <>
                    {" · "}
                    <span className="font-medium text-palette-gray-primary">
                      {wholeMoney(util.upcoming)}
                    </span>{" "}
                    more in charges before {fmtShortDate(util.dueDate)}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── The two directions ────────────────────────────────── */}
      <div className="grid gap-3 border-t border-palette-amber-border/60 p-3 md:grid-cols-2">
        <div className="rounded-lg border border-palette-gray-divider bg-white p-3">
          <ChargeList charges={detail.charges} onEditCharge={onEditCharge} />
        </div>
        <div className="rounded-lg border border-palette-gray-divider bg-white p-3">
          <PaymentList detail={detail} onEditStatement={editStatement} />
        </div>
      </div>
    </div>
  );
}

/**
 * A list heading, coloured by the DIRECTION its list moves money — rose for what
 * the card is charged, emerald for what is paid into it. Same convention the
 * grid above uses for Outflows and Inflows, so the panel is read the same way.
 */
function SectionTitle({ tone = "rose", children }) {
  const tones = {
    rose: "text-palette-rose-heading",
    emerald: "text-palette-emerald-text",
  };
  return (
    <div
      className={`mb-2 text-[11px] font-semibold uppercase tracking-wide ${tones[tone] ?? tones.rose}`}
    >
      {children}
    </div>
  );
}

function ChargeList({ charges, onEditCharge }) {
  return (
    <>
      <SectionTitle>Charged to the card</SectionTitle>
      {charges.length === 0 ? (
        <div className="py-2 text-sm text-palette-gray-placeholder">
          Nothing is scheduled onto this card in the statement period.
        </div>
      ) : (
        <ul className="divide-y divide-palette-gray-divider/70">
          {charges.map((c) => (
            <Row
              key={c.key}
              title={c.date}
              editLabel={`Edit the schedule behind ${c.vendor ?? c.category}`}
              onClick={onEditCharge ? () => onEditCharge(c) : undefined}
            >
              <div className="flex min-w-0 gap-2">
                <Dot className="mt-1.5 bg-palette-rose-indicator" />
                <div className="min-w-0">
                  <div className="truncate text-sm text-palette-gray-primary">
                    {c.vendor ? (
                      <>
                        {c.vendor}{" "}
                        <span className="text-palette-gray-secondary">· {c.category}</span>
                      </>
                    ) : (
                      c.category
                    )}
                  </div>
                  <div className="text-xs text-palette-gray-secondary">Day {c.day}</div>
                </div>
              </div>
              <div className="shrink-0 text-sm font-medium tabular-nums text-smiles-redOrange">
                {money(-c.amount)}
              </div>
            </Row>
          ))}
        </ul>
      )}
    </>
  );
}

function PaymentList({ detail, onEditStatement }) {
  const { payments, payerLabel } = detail;
  return (
    <>
      <SectionTitle tone="emerald">Bank → card payments</SectionTitle>
      {payments.length === 0 ? (
        <div className="py-2 text-sm text-palette-gray-placeholder">
          This statement has no payment plan yet.
          {onEditStatement && (
            <button
              type="button"
              onClick={onEditStatement}
              className="ml-1 font-medium text-palette-indigo-heading underline underline-offset-2 hover:text-palette-indigo-badge"
            >
              Add one
            </button>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-palette-gray-divider/70">
          {payments.map((p) => (
            <Row
              key={`${p.index}-${p.date}`}
              title={`Payment ${p.index + 1} of ${p.count}`}
              editLabel={`Edit this statement — payment ${p.index + 1} of ${p.count}`}
              onClick={onEditStatement}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    p.paid
                      ? "bg-palette-emerald-muted text-palette-emerald-text"
                      : "bg-palette-emerald-soft text-palette-emerald-badge"
                  }`}
                >
                  {fmtShortDate(p.date)}
                </span>
                <span className="truncate text-sm text-palette-gray-primary">
                  {payerLabel ? `Transfer from ${payerLabel}` : "Transfer"}
                  {p.count > 1 && (
                    <span className="text-palette-gray-secondary">
                      {" "}
                      · payment {p.index + 1} of {p.count}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`text-sm tabular-nums text-smiles-green ${
                    p.paid ? "font-semibold" : "font-medium"
                  }`}
                >
                  {money(p.amount, { signed: true })}
                </span>
                {p.paid ? (
                  <Pill tone="emerald" title="Dated before today — counted in Paid so far">
                    paid
                  </Pill>
                ) : p.afterDue ? (
                  <Pill
                    tone="rose"
                    title={`Lands after the ${fmtShortDate(detail.dueDate)} due date`}
                  >
                    after due
                  </Pill>
                ) : (
                  <Pill tone="emerald">scheduled</Pill>
                )}
              </div>
            </Row>
          ))}
        </ul>
      )}
    </>
  );
}
